import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type {
	CycleSection,
	MoneyBlock,
	ShopPerformanceResponse,
} from '../../../../redux/api/shop-performance-report';
import { deltaCopy, formatDollars, formatPct } from './format';
import { HISTOGRAM_BUCKETS } from './histogramBuckets';
import type { ShopPerformanceFilters } from './queryParams';

dayjs.extend(utc);
dayjs.extend(timezone);

const CHICAGO = 'America/Chicago';

const AVERAGE_ROWS: Array<{
	key: keyof CycleSection['averages'];
	label: string;
}> = [
	{ key: 'acceptance', label: 'Time to acceptance' },
	{ key: 'material_prep', label: 'Time to material prep' },
	{ key: 'kitting', label: 'Time to kitting' },
	{ key: 'machine_setup', label: 'Time to machine setup' },
	{ key: 'machining_complete', label: 'Time to machining complete' },
	{ key: 'delivery', label: 'Time to delivery' },
	{ key: 'lead_time', label: 'Planned lead time' },
];

const BAR_SIDE: Record<string, string> = Object.fromEntries(
	HISTOGRAM_BUCKETS.map((item) => [item.key, item.side])
);

const listOrAll = (values: string[], fallback: string) =>
	values.length ? values.join(', ') : fallback;

const boundPhrase = (min: number | undefined, max: number | undefined, noun: string) => {
	if (min === undefined && max === undefined) return `All ${noun}`;
	if (min !== undefined && max !== undefined) return `${noun} ${min}–${max}`;
	if (min !== undefined) return `${noun} ≥ ${min}`;
	return `${noun} ≤ ${max}`;
};

const sitePhrase = (buildingIds: string[], buildingNames: string[]) => {
	if (!buildingIds.length) return 'All sites';
	if (buildingNames.length) return buildingNames.join(', ');
	return `${buildingIds.length} site${buildingIds.length === 1 ? '' : 's'} selected`;
};

interface PrintableReportProps {
	data: ShopPerformanceResponse;
	filters: ShopPerformanceFilters;
	buildingNames: string[];
}

const MoneySection = ({
	title,
	block,
	showMoney,
}: {
	title: string;
	block: MoneyBlock;
	showMoney: boolean;
}) => (
	<>
		<div className="shop-performance-print__heading">{title}</div>
		<table className="shop-performance-print__data">
			<thead>
				<tr>
					<th>Total</th>
					<th className="shop-performance-print__num">Orders</th>
					<th className="shop-performance-print__num">Lines</th>
					<th className="shop-performance-print__num">Material cost</th>
				</tr>
			</thead>
			<tbody>
				<tr>
					<td className="shop-performance-print__num">
						{showMoney ? formatDollars(block.total) : '—'}
					</td>
					<td className="shop-performance-print__num">{block.orders}</td>
					<td className="shop-performance-print__num">{block.lines}</td>
					<td className="shop-performance-print__num">
						{showMoney
							? `${formatDollars(block.material_cost)} (${formatPct(block.material_cost_pct)})`
							: '—'}
					</td>
				</tr>
			</tbody>
		</table>
		<table className="shop-performance-print__data">
			<thead>
				<tr>
					<th>Planned lead time</th>
					<th className="shop-performance-print__num">Lines</th>
					<th className="shop-performance-print__num">Total</th>
					<th className="shop-performance-print__num">Line share</th>
				</tr>
			</thead>
			<tbody>
				{block.by_lead_time.map((bucket) => {
					const share =
						block.lines === 0 ? '0' : String(Math.round((bucket.lines / block.lines) * 100));
					return (
						<tr key={bucket.key}>
							<td>{bucket.label}</td>
							<td className="shop-performance-print__num">{bucket.lines}</td>
							<td className="shop-performance-print__num">
								{showMoney ? formatDollars(bucket.total) : '—'}
							</td>
							<td className="shop-performance-print__num">{formatPct(share)}</td>
						</tr>
					);
				})}
			</tbody>
		</table>
		<table className="shop-performance-print__data">
			<thead>
				<tr>
					<th>Quantity</th>
					<th className="shop-performance-print__num">Lines</th>
					<th className="shop-performance-print__num">Total</th>
					<th className="shop-performance-print__num">$ share</th>
				</tr>
			</thead>
			<tbody>
				{block.by_quantity.map((bucket) => {
					const whole = Number(block.total ?? 0);
					const part = Number(bucket.total ?? 0);
					const share = !whole ? '0' : String(Math.round((part / whole) * 100));
					return (
						<tr key={bucket.key}>
							<td>{bucket.label}</td>
							<td className="shop-performance-print__num">{bucket.lines}</td>
							<td className="shop-performance-print__num">
								{showMoney ? formatDollars(bucket.total) : '—'}
							</td>
							<td className="shop-performance-print__num">
								{showMoney ? formatPct(share) : '—'}
							</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	</>
);

const PrintHistogram = ({ section }: { section: CycleSection }) => {
	const maxCount = Math.max(1, ...section.histogram.map((bar) => bar.count));
	return (
		<div className="shop-performance-print__hist">
			<div className="shop-performance-print__ontime">
				On or before ship date {formatPct(section.on_time_pct)} · {section.on_time_n} of{' '}
				{section.histogram_n} delivered lines with a ship date
				{section.line_count > section.histogram_n
					? ` · ${section.line_count - section.histogram_n} not counted (undelivered or no ship date)`
					: ''}
			</div>
			{section.histogram.map((bar) => (
				<div key={bar.key} className="shop-performance-print__bar-row">
					<span className="shop-performance-print__bar-label">{bar.label}</span>
					<span className="shop-performance-print__bar-track">
						<span
							className={`shop-performance-print__bar shop-performance-print__bar--${BAR_SIDE[bar.key] ?? 'on_time'}`}
							style={{ width: `${(bar.count / maxCount) * 100}%` }}
						/>
					</span>
					<span className="shop-performance-print__num">{bar.count}</span>
				</div>
			))}
		</div>
	);
};

const CycleTables = ({ title, section }: { title: string; section: CycleSection }) => (
	<>
		<div className="shop-performance-print__heading">{title}</div>
		<table className="shop-performance-print__data">
			<thead>
				<tr>
					<th>Stage</th>
					<th className="shop-performance-print__num">Days</th>
					<th className="shop-performance-print__num">Lines</th>
					<th className="shop-performance-print__num">vs prior</th>
				</tr>
			</thead>
			<tbody>
				{AVERAGE_ROWS.map((row) => {
					const metric = section.averages[row.key];
					return (
						<tr key={row.key}>
							<td>{row.label}</td>
							<td className="shop-performance-print__num">{metric.days ?? '—'}</td>
							<td className="shop-performance-print__num">{metric.n}</td>
							<td className="shop-performance-print__num">{deltaCopy(metric.delta_days)}</td>
						</tr>
					);
				})}
			</tbody>
		</table>
		<PrintHistogram section={section} />
	</>
);

const PrintableReport = ({ data, filters, buildingNames }: PrintableReportProps) => {
	const generated = dayjs().tz(CHICAGO).format('MMM D, YYYY HH:mm z');
	const showMoney = data.include_revenue;

	return createPortal(
		<div className="shop-performance-print">
			<table className="shop-performance-print__page">
				<thead>
					<tr>
						<td className="shop-performance-print__header">
							<div className="shop-performance-print__title">Shop Performance</div>
							<div className="shop-performance-print__meta">
								<span>
									{filters.dateFrom} to {filters.dateTo} · vs prior {data.prior_date_from} to{' '}
									{data.prior_date_to} · {listOrAll(filters.methods, 'All methods')} ·{' '}
									{sitePhrase(filters.buildings, buildingNames)} ·{' '}
									{listOrAll(filters.materials, 'All materials')} ·{' '}
									{listOrAll(filters.finishes, 'All finishes')} ·{' '}
									{listOrAll(filters.coatings, 'All coatings')} ·{' '}
									{boundPhrase(filters.leadTimeMin, filters.leadTimeMax, 'lead time')} ·{' '}
									{boundPhrase(filters.qtyMin, filters.qtyMax, 'qty')}
								</span>
								<span>{generated}</span>
							</div>
						</td>
					</tr>
				</thead>
				<tfoot>
					<tr>
						<td className="shop-performance-print__footer">
							PartsBadger · Shop Performance · {generated}. A job received and
							delivered in the same range is counted in both sections.{' '}
							{data.job_cost_footnote}
						</td>
					</tr>
				</tfoot>
				<tbody>
					<tr>
						<td className="shop-performance-print__body">
							<MoneySection
								title="Sales (received in range)"
								block={data.sales}
								showMoney={showMoney}
							/>
							<MoneySection
								title="Revenue (delivered in full in range)"
								block={data.revenue}
								showMoney={showMoney}
							/>

							<div className="shop-performance-print__heading">Other material costs</div>
							<table className="shop-performance-print__data">
								<thead>
									<tr>
										<th>Code</th>
										<th className="shop-performance-print__num">Cost</th>
									</tr>
								</thead>
								<tbody>
									{data.other_costs.by_code.length ? (
										data.other_costs.by_code.map((row) => (
											<tr key={row.code}>
												<td>{row.label}</td>
												<td className="shop-performance-print__num">
													{showMoney ? formatDollars(row.cost) : '—'}
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={2}>No correction costs logged.</td>
										</tr>
									)}
								</tbody>
							</table>

							<CycleTables
								title="Based on received date"
								section={data.by_received}
							/>
							<CycleTables
								title="Based on delivered-in-full date"
								section={data.by_delivered}
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</div>,
		document.body
	);
};

export default PrintableReport;
